import _ from "lodash";
import { getOrganizationCurriculumProgramsQuery } from "modules/CommonQuery";
import {
  getAcademicYearsList,
  DESIGNATION_CONSTANTS_MAP,
} from "modules/Services";
import moment from "moment";
import ACLStore from "lib/aclStore";
import { ENABLE_CROSS_CURRICULUM_STUDENTS } from "Constants/permissionConstants";
import { getSelectedAcademicYearId } from "SchoolSetup/modules/Utils";
import { isUserMiddleNameEnabled } from "Platform/modules/PlatformUtils";
import {
  AddStaffIllustration,
  AddStudentIllustration,
} from "@toddle-edu/ds-theme";
import { getOrganizationCurriculumProgramsByAcademicYearQuery } from "Login/routes/Administrator/components/AcademicYearTransitionConfiguration/modules/Query";
import { getStaffFilter } from "StudentSetup/modules/StudentsModule";
import { getUserCourseMapFilters } from "Administrator/modules/Utils";

export const EMPTY_OBJECT = {};
export const EMPTY_ARRAY = [];

export const isPronounsVisible = () => {
  return (
    ACLStore.can("FeatureFlag:EnableStudentPronounsInSchoolSetup") &&
    !isUserMiddleNameEnabled()
  );
};

const getEntityLabel = ({
  modalType,
  isPlural = false,
  t,
  isHeaderTitle = false,
}) => {
  switch (modalType) {
    case "COURSE":
      return isPlural ? t("common:course_plural") : t("common:course");
    case "LEARNING_COURSE":
      return isPlural ? t("course:courses") : t("course:course");
    case "STAFF":
      return isHeaderTitle ? t("schoolSetup:staff_member") : t("common:staff");
    case "STUDENT":
      return t("common:student");
    case "VISITOR":
      return t("snp:visitor");
  }
};

const getDialogueBoxContent = ({ t, modalType, mode }) => {
  const isEditMode = mode === "EDIT";
  const entity = getEntityLabel({ modalType, t, isHeaderTitle: true });
  let title, modalBody;
  switch (modalType) {
    case "COURSE":
      title = t("common:discard_label", { label: entity });
      modalBody = isEditMode
        ? t("schoolSetup:cancel_edit_entity_discard_msg_body", { entity })
        : t("schoolSetup:cancel_class_creation_discard_msg_body");
      break;
    default:
      title = t("common:discard_changes");
      modalBody = isEditMode
        ? t("schoolSetup:cancel_edit_entity_discard_msg_body", { entity })
        : t("schoolSetup:cancel_entity_creation_discard_msg_body", {
            entity,
          });
  }
  return {
    title,
    modalBody,
    button1: t("common:keep_editing"),
    button2: t("common:discard"),
    button2Props: {
      variant: "destructive",
      size: "large",
    },
  };
};

const getDropdownValues = dropdownValue => {
  if (_.isArray(dropdownValue)) return _.map(dropdownValue, "value");
  return _.get(dropdownValue, "value", null);
};

const getModelTypeCheckObject = ({ modalType }) => {
  const isClassModal = _.isEqual(modalType, "COURSE");
  const isStudentModal = _.isEqual(modalType, "STUDENT");
  const isStaffModal = _.isEqual(modalType, "STAFF");
  const isVisitorModal = _.isEqual(modalType, "VISITOR");
  const isLearningCourseModal = modalType === "LEARNING_COURSE";

  return {
    isClassModal,
    isStudentModal,
    isStaffModal,
    isVisitorModal,
    isLearningCourseModal,
  };
};

const getModalTabCheckObject = ({ modalTab }) => {
  const isAddStudentTab = _.isEqual(modalTab, "ADD_STUDENTS");
  const isAddStaffTab = _.isEqual(modalTab, "ADD_STAFF");
  const isAddClassesTab = _.isEqual(modalTab, "ADD_TO_CLASSES");
  const isHomeRoomAdvisorTab = _.isEqual(modalTab, "HOMEROOM_ADVISOR");
  const isCyclesTab = _.isEqual(modalTab, "ADD_CYCLES");

  return {
    isAddStudentTab,
    isAddStaffTab,
    isAddClassesTab,
    isHomeRoomAdvisorTab,
    isCyclesTab,
  };
};

const checkIfDesignationSchoolAdmin = ({ designation }) => {
  return _.isEqual(designation, DESIGNATION_CONSTANTS_MAP.SCHOOL_ADMIN.id);
};

const getSubjectLevelConstants = ({
  subjectLevels,
  item,
  modalType,
  modalTab,
}) => {
  if (modalTab === "ADD_STUDENTS") {
    return {
      showSubjectLevelRadios: _.size(subjectLevels) > 1,
      entitySubjectLevels: subjectLevels,
    };
  } else if (modalTab === "ADD_TO_CLASSES") {
    const entitySubjectLevels = getCourseLevelOptions({ item });
    return {
      showSubjectLevelRadios:
        _.size(entitySubjectLevels) > 0 && modalType === "STUDENT",
      entitySubjectLevels,
    };
  }

  return {};
};

const getCourseLevelOptions = ({ item }) => {
  const subjectLevelOptions = _.map(
    _.filter(item?.genericTags, ({ type }) => type == "SUBJECT_LEVEL"),
    ({ id, label }) => {
      return {
        key: id,
        value: id,
        label,
      };
    }
  );
  return subjectLevelOptions;
};

const getEntityListNodes = (items, sortBySubjectGrp) => {
  const entities = _.map(items, ({ node }) => {
    return node;
  });

  if (!sortBySubjectGrp) {
    return entities;
  }

  return _.orderBy(entities, ({ subjects }) => {
    const subjectGroup = _.get(subjects, "[0].subjectGroup", {});
    return _.get(subjectGroup, "displaySequence");
  });
};

const getRefetchQueriesForAcademicYearTransition = ({ getState }) => {
  const state = getState();
  const organizationId = _.get(state, "login.userInfo.org_id");
  const selectedAcademicYearId = getSelectedAcademicYearId(state);

  return {
    query: getOrganizationCurriculumProgramsQuery,
    variables: {
      organizationId,
      academicYearId: selectedAcademicYearId,
      entityFilters: {
        entityTypes: ["COURSE", "STAFF", "STUDENT"],
        academicYearId: selectedAcademicYearId,
      },
    },
  };
};

const getRefetchQueriesForFutureAYCurriculumPrograms = ({
  organizationId,
  academicYearId,
}) => {
  return {
    query: getOrganizationCurriculumProgramsByAcademicYearQuery,
    variables: {
      organizationId,
      academicYearId,
      entityFilters: {
        entityTypes: ["COURSE", "STAFF", "STUDENT"],
        academicYearId,
      },
    },
  };
};

const getAcademicYears = params => {
  const { organizationId, currentAcademicYear } = params;

  const academicYearsList =
    params?.academicYearsList || getAcademicYearsList(organizationId);
  const currentAcademicYearStartDate = _.get(currentAcademicYear, "startDate");
  const currentAcademicYearEndDate = _.get(currentAcademicYear, "endDate");

  const prevAcademicYears = _.head(
    _.orderBy(
      _.filter(academicYearsList, academicYear =>
        moment(academicYear.startDate).isBefore(currentAcademicYearStartDate)
      ),
      o => moment(o.startDate).format("YYYYMMDD"),
      "desc"
    )
  );

  const nextAcademicYears = _.head(
    _.orderBy(
      _.filter(academicYearsList, academicYear =>
        moment(academicYear.endDate).isAfter(currentAcademicYearEndDate)
      ),
      o => moment(o.startDate).format("YYYYMMDD"),
      "asc"
    )
  );

  const currentAcademicYears = _.head(
    _.filter(
      academicYearsList,
      academicYear =>
        moment(academicYear.startDate).isSameOrBefore(
          currentAcademicYearStartDate
        ) &&
        moment(academicYear.endDate).isSameOrAfter(currentAcademicYearEndDate)
    )
  );

  return {
    prevAcademicYear: prevAcademicYears,
    nextAcademicYear: nextAcademicYears,
    currentAcademicYear: currentAcademicYears,
  };
};
const getAcademicYearsValue = ({
  grade,
  currentAcademicYear,
  nextAcademicYear,
  prevAcademicYear,
}) => {
  if (grade && grade?.globalGrade?.uid === "Grade 11") {
    return [currentAcademicYear, nextAcademicYear];
  } else {
    return [prevAcademicYear, currentAcademicYear];
  }
};

const getEmptyStateTextAndIllustrations = ({
  selectedCurriculumDetails,
  selectedCurriculumProgramAcronym,
  nextAcademicYearLabel,
  modalTab,
  isFutureAcademicYearSelected,
  t,
}) => {
  let illustration, entity, entityPlural;
  let isConfigurationAllowed = true;
  if (isFutureAcademicYearSelected) {
    switch (modalTab) {
      case "ADD_STUDENTS":
        illustration = AddStudentIllustration;
        entity = t("common:student");
        entityPlural = t("common:student_plural");
        isConfigurationAllowed = _.get(
          _.find(
            _.get(
              selectedCurriculumDetails,
              "academicYearTransitionConfigurationState"
            ),
            ({ state }) => _.isEqual(state, "STUDENT_CONFIGURATION")
          ),
          "isComplete",
          false
        );
        break;
      case "ADD_STAFF":
        illustration = AddStaffIllustration;
        entity = t("common:staff");
        entityPlural = t("common:staff");
        isConfigurationAllowed = _.get(
          _.find(
            _.get(
              selectedCurriculumDetails,
              "academicYearTransitionConfigurationState"
            ),
            ({ state }) => _.isEqual(state, "STAFF_CONFIGURATION")
          ),
          "isComplete",
          false
        );
        break;
    }
  }
  const title = t(
    "schoolSetup:curriculum_entity_transition_not_completed_title",
    {
      curriculum: selectedCurriculumProgramAcronym,
      entity,
    }
  );
  const subTitle = t(
    "schoolSetup:curriculum_entity_transition_not_completed_subTitle",
    {
      curriculum: selectedCurriculumProgramAcronym,
      entity,
      academicYearLabel: nextAcademicYearLabel,
      entityPlural,
    }
  );
  return {
    emptyStateIllustration: illustration,
    emptyStateTitle: title,
    emptyStateSubTitle: subTitle,
    isConfigurationAllowed,
  };
};

const getStudentDetailsFilters = ({ entityId, academicYearId }) => {
  return {
    userId: entityId,
    userArchivedFilters: {
      academicYearId,
    },
    studentCurriculumProgramFilter: {
      academicYearId,
    },
    userYearGroupMapFilters: {
      academicYearIds: [academicYearId],
    },
    yearGroupGradeFilters: {
      academicYearId,
    },
    staffFilter: getStaffFilter({
      id: academicYearId,
    }),
    withStaffRelation: true,
    userCourseMapFilters: getUserCourseMapFilters({
      archivalState: "ALL",
      academicYearIds: [academicYearId],
    }),
    courseGradeFilters: {
      academicYearId,
    },
  };
};

const enableCrossCurriculumStudents = () =>
  ACLStore.can(ENABLE_CROSS_CURRICULUM_STUDENTS);

export {
  getEntityLabel,
  getDropdownValues,
  getDialogueBoxContent,
  getModalTabCheckObject,
  getModelTypeCheckObject,
  getCourseLevelOptions,
  getSubjectLevelConstants,
  getEntityListNodes,
  getRefetchQueriesForAcademicYearTransition,
  getRefetchQueriesForFutureAYCurriculumPrograms,
  getAcademicYears,
  getAcademicYearsValue,
  checkIfDesignationSchoolAdmin,
  enableCrossCurriculumStudents,
  getEmptyStateTextAndIllustrations,
  getStudentDetailsFilters,
};
